import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Music, Video, DollarSign, Shield, ArrowRight, Upload, 
  Disc, Radio, Swords, Trophy, Headphones, Star, Bot,
  ChevronDown, ChevronUp, Zap, Users, Play, Heart,
  Sliders, Calendar, Crown, MessageCircle, Globe, Mic,
  Package, Sparkles, FileCheck
} from 'lucide-react';
import CreatorSpotlight from '../components/CreatorSpotlight';
import FeaturedPromotions from '../components/FeaturedPromotions';
import SongWarsChampionsSlideshow from '../components/SongWarsChampionsSlideshow';
import SEOHead from '../components/common/SEOHead';
import NewsletterSignup from '../components/common/NewsletterSignup';
import OnAirNowTicker from '../components/home/OnAirNowTicker';
import OneVoiceRadarStrip from '../components/home/OneVoiceRadarStrip';
import UpcomingSessionsStrip from '../components/home/UpcomingSessionsStrip';
import AllGenreRadioHero from '../components/AllGenreRadioHero';
import { useSeasonalTheme } from '../hooks/useSeasonalTheme';

// Background image - concert crowd
const HERO_BG = 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg';

export default function Landing() {
  const navigate = useNavigate();
  const [expandedFeature, setExpandedFeature] = useState(null);
  const seasonal = useSeasonalTheme();
  
  // Tour is now handled by OnboardingFlow in App.js

  // Artist circles for social proof
  const artistCircles = ['A', 'B', 'C', 'D', 'E'];

  // Platform Features with detailed descriptions
  const platformFeatures = [
    {
      id: 'music-distribution',
      icon: Upload,
      title: 'Music Distribution',
      shortDesc: 'Upload and sell tracks, beats & albums globally',
      color: '#00CCFF',
      fullDesc: 'Upload your music, set your price, and start selling instantly. Artists keep ~90% of every sale. Support for tracks, albums, beats, and video content. Automated AI verification ensures your content is protected.',
      stats: [
        { label: 'Artist Revenue', value: '~90%' },
        { label: 'Content Types', value: '4+' },
        { label: 'Global Reach', value: 'Worldwide' }
      ]
    },
    {
      id: 'beat-marketplace',
      icon: Disc,
      title: 'Beat Marketplace',
      shortDesc: 'Buy and sell beats with licensing',
      color: '#00ff88',
      fullDesc: 'Producers can list beats for sale with multiple licensing tiers. Buyers can preview, purchase, and download beats instantly. Built-in license agreement signing ensures both parties are protected.',
      stats: [
        { label: 'License Types', value: '3' },
        { label: 'Instant Download', value: 'Yes' },
        { label: 'Producer Revenue', value: '~90%' }
      ]
    },
    {
      id: 'mix-master',
      icon: Sliders,
      title: 'Mix & Master Services',
      shortDesc: 'Hire producers for professional mixing & mastering',
      color: '#FFDA33',
      fullDesc: 'Connect with professional producers for mixing and mastering services. Secure escrow payments, file delivery, revision rounds, and 5-day delivery guarantee. 10% + $0.25 platform fee keeps pricing transparent.',
      stats: [
        { label: 'Delivery Time', value: '5 Days' },
        { label: 'Revisions', value: '2 Rounds' },
        { label: 'Secure Payment', value: 'Escrow' }
      ]
    },
    {
      id: 'song-wars',
      icon: Swords,
      title: 'Song Wars',
      shortDesc: 'Compete in tournaments for cash prizes',
      color: '#FF69B4',
      fullDesc: 'Enter your tracks into head-to-head battles against other artists. Viewers vote on the winner, and champions take home real cash prizes. Weekly tournaments with different genres and prize pools. Build your reputation and climb the leaderboard!',
      stats: [
        { label: 'Weekly Prizes', value: '$500+' },
        { label: 'Active Tournaments', value: '24/7' },
        { label: 'Winners Paid', value: '100+' }
      ]
    },
    {
      id: 'music-review',
      icon: Headphones,
      title: 'Music Review',
      shortDesc: 'Get feedback from industry reviewers',
      color: '#00CCFF',
      fullDesc: 'Submit your tracks to professional reviewers for detailed, honest feedback. Get insights on production quality, mixing, songwriting, and commercial potential. Build connections with industry professionals who can help elevate your career.',
      stats: [
        { label: 'Reviewers', value: '50+' },
        { label: 'Tracks Reviewed', value: '10K+' },
        { label: 'Avg Response', value: '24hrs' }
      ]
    },
    {
      id: 'vip-membership',
      icon: Crown,
      title: 'VIP Membership',
      shortDesc: 'Unlock premium features and ad-free listening',
      color: '#FFDA33',
      fullDesc: 'Go VIP for exclusive perks: ad-free streaming, priority queue in reviews, unlimited uploads, analytics dashboard, and early access to new features. Multiple plans from monthly to annual with free trial options.',
      stats: [
        { label: 'Ad-Free', value: 'Yes' },
        { label: 'Free Trial', value: 'Up to 30d' },
        { label: 'Plans', value: '4' }
      ]
    },
    {
      id: 'video-streaming',
      icon: Video,
      title: 'Video Streaming',
      shortDesc: 'Upload and stream music videos for free',
      color: '#9b59b6',
      fullDesc: 'Upload music videos, visualizers, and behind-the-scenes content. Free video streaming for all users. Build your visual brand and reach new audiences through video content.',
      stats: [
        { label: 'Free Streaming', value: 'Yes' },
        { label: 'Upload Limit', value: 'Unlimited' },
        { label: 'Video Quality', value: 'HD' }
      ]
    },
    {
      id: 'free-radio',
      icon: Radio,
      title: 'Free Radio',
      shortDesc: 'Stream music 24/7 for free',
      color: '#e67e22',
      fullDesc: 'Listen to unlimited music streaming with our curated radio stations. Choose from Music Radio, Beats Radio, or AI Radio. Discover new artists, create playlists, and enjoy ad-supported free listening anywhere, anytime.',
      stats: [
        { label: 'Stations', value: '3' },
        { label: '24/7 Streams', value: 'Yes' },
        { label: 'New Songs/Day', value: '100+' }
      ]
    },
    {
      id: 'ai-radio',
      icon: Bot,
      title: 'AI Radio',
      shortDesc: 'Discover AI-generated music',
      color: '#ff6b6b',
      fullDesc: 'Explore a dedicated space for AI-generated music. Artists can showcase their AI-assisted creations with proper labeling and transparency. Discover cutting-edge sounds and experimental compositions from the future of music.',
      stats: [
        { label: 'AI Tracks', value: '1K+' },
        { label: 'Daily Plays', value: '5K+' },
        { label: 'AI Artists', value: '200+' }
      ]
    },
    {
      id: 'events',
      icon: Calendar,
      title: 'Events & Tickets',
      shortDesc: 'Create events and sell tickets',
      color: '#e17055',
      fullDesc: 'Host concerts, album release parties, listening sessions, and more. Sell tickets directly through the platform, manage RSVPs, and promote your events to the community. Integrated ticket marketplace for reselling.',
      stats: [
        { label: 'Event Types', value: '5+' },
        { label: 'Ticket Sales', value: 'Built-in' },
        { label: 'Marketplace', value: 'Yes' }
      ]
    },
    {
      id: 'communities',
      icon: MessageCircle,
      title: 'Communities',
      shortDesc: 'Connect with other artists and fans',
      color: '#00CCFF',
      fullDesc: 'Join genre-based communities, share your work, collaborate with other artists, and engage with fans. Post updates, share tracks, and build your network in a supportive music community.',
      stats: [
        { label: 'Communities', value: '50+' },
        { label: 'Collaboration', value: 'Built-in' },
        { label: 'Direct Messages', value: 'Yes' }
      ]
    },
    {
      id: 'discover',
      icon: Globe,
      title: 'Artist & Producer Discovery',
      shortDesc: 'Find and connect with talent worldwide',
      color: '#00ff88',
      fullDesc: 'Browse and discover artists, producers, DJs, and promoters from around the world. Filter by genre, location, and style. Follow your favorites and stay updated on new releases and events.',
      stats: [
        { label: 'Artists', value: '10K+' },
        { label: 'Producers', value: '5K+' },
        { label: 'Global', value: 'Worldwide' }
      ]
    },
    {
      id: 'leaderboard',
      icon: Trophy,
      title: 'Leaderboard & Rankings',
      shortDesc: 'Track your progress and compete for the top',
      color: '#FFDA33',
      fullDesc: 'See how you stack up against other artists with weekly and all-time leaderboards. Rankings based on plays, sales, followers, and Song Wars victories. Rise through the ranks and get noticed.',
      stats: [
        { label: 'Updated', value: 'Weekly' },
        { label: 'Categories', value: '5+' },
        { label: 'Top Tracks', value: 'Ranked' }
      ]
    },
    {
      id: 'merch-marketplace',
      icon: Package,
      title: 'Merch Marketplace',
      shortDesc: 'Sell physical merch with print-on-demand fulfillment',
      color: '#00ff88',
      fullDesc: 'Open your own merch store directly inside your artist page. T-shirts, hoodies, vinyl, posters, and digital downloads. Stripe Connect splits revenue automatically — Creator Pro Plus members get the platform fee waived. Built-in disputes and refund flow.',
      stats: [
        { label: 'Product Types', value: '12+' },
        { label: 'Plus Fee Waiver', value: 'Yes' },
        { label: 'Auto Splits', value: 'Stripe Connect' },
      ]
    },
    {
      id: 'onevoice-studio',
      icon: Mic,
      title: 'One Voice Studio (DAW)',
      shortDesc: 'Browser-based studio with stems, effects & multitrack',
      color: '#00CCFF',
      fullDesc: 'Record, layer, and mix tracks directly in your browser. Multitrack timeline, effects rack, AI-assisted vocal cleanup, and one-click export straight into the upload pipeline. No installs — runs on desktop and mobile.',
      stats: [
        { label: 'Tracks', value: 'Unlimited' },
        { label: 'Export', value: '1-Click → Upload' },
        { label: 'Effects', value: 'Pro Suite' },
      ]
    },
    {
      id: 'listening-parties',
      icon: Radio,
      title: 'Listening Parties',
      shortDesc: 'Host live release parties for your fans (Free or paid)',
      color: '#FF69B4',
      fullDesc: 'Schedule a synchronized listening event for any album or track. Free or ticketed (starting at $0). Fans join a live chat, react in real time, and unlock early-bird perks. Built-in scheduling and reminder notifications.',
      stats: [
        { label: 'Min Price', value: '$0 (Free)' },
        { label: 'Live Chat', value: 'Synced' },
        { label: 'RSVPs', value: 'Unlimited' },
      ]
    },
    {
      id: 'creator-pro-plus',
      icon: Sparkles,
      title: 'Creator Pro & Plus',
      shortDesc: 'Skip the 48h wait + priority queue + custom landing pages',
      color: '#FFDA33',
      fullDesc: 'Pro members publish instantly (no 48-hour delay), get priority review queue, 2x featured slots, Fan Insights, push notifications, AND a custom landing page at onevoicedistro.com/p/<your-username> with click analytics. Plus members add direct support email and waived merch fees.',
      stats: [
        { label: 'Instant Publish', value: 'Yes' },
        { label: 'Featured Slots', value: '2x' },
        { label: 'Landing Page', value: '/p/<you>' },
      ]
    },
    {
      id: 'isrc-upc',
      icon: FileCheck,
      title: 'Auto ISRC & UPC',
      shortDesc: 'Industry codes minted automatically on every release',
      color: '#00CCFF',
      fullDesc: 'Every track gets a real ISRC (International Standard Recording Code) and every album gets a real UPC barcode — minted automatically on upload, registered to your account, and exportable for DSP pitches, royalty matching, and copyright registration.',
      stats: [
        { label: 'ISRC per Track', value: 'Auto' },
        { label: 'UPC per Album', value: 'Auto' },
        { label: 'DSP-Ready', value: 'Yes' },
      ]
    },
    {
      id: 'split-payouts',
      icon: DollarSign,
      title: 'Stripe Connect Splits',
      shortDesc: 'Auto-split revenue with collaborators on every sale',
      color: '#00ff88',
      fullDesc: 'Configure royalty splits with featured artists, producers, and writers — payouts happen automatically to each collaborator\'s connected Stripe account on every sale. KYC-gated withdrawals keep the platform compliant.',
      stats: [
        { label: 'Auto Splits', value: 'Per Sale' },
        { label: 'Connected Accounts', value: 'Unlimited' },
        { label: 'Payout Speed', value: 'Real-time' },
      ]
    },
    {
      id: 'safe-harbor',
      icon: Shield,
      title: 'Section 230 Safe Harbor',
      shortDesc: 'Built-in copyright + DMCA infrastructure protects creators',
      color: '#9b59b6',
      fullDesc: 'Every upload requires a Content Provider acknowledgment under 47 U.S.C. § 230(f)(3), and every claim flows through a built-in DMCA / counter-notice queue. The receipts ledger protects both the artist and the platform.',
      stats: [
        { label: 'Ack on Upload', value: 'Required' },
        { label: 'DMCA Queue', value: 'Live' },
        { label: 'Receipts Ledger', value: 'Per Sale' },
      ]
    },
  ];

  // Scroll to pricing section
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Toggle feature expansion
  const toggleFeature = (featureId) => {
    setExpandedFeature(expandedFeature === featureId ? null : featureId);
  };

  return (
    <div className="preserve-dark min-h-screen bg-black">
      <SEOHead
        title="Independent Music Distribution"
        description="One voice. Your choice. Press play. Get paid. Upload, distribute, and monetize your music — keep ~90% of every sale. Song Wars, live reviews, beat marketplace, and more."
      />
      
      {/* ============= HEADER ============= */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center justify-between px-4 md:px-8 py-4">
          <img
            src="/onevoice-logo-circular.png"
            alt="One Voice Distribution"
            className="h-16 md:h-20 w-auto object-contain drop-shadow-[0_0_18px_rgba(255,218,51,0.35)]"
            data-testid="header-logo"
          />
          <div className="flex items-center gap-3">
            <Button
              data-testid="login-btn"
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-[#00CCFF] hover:text-[#00E5FF] font-mono uppercase tracking-wider text-sm"
            >
              Login
            </Button>
            <Button
              data-testid="register-btn"
              onClick={() => navigate('/register')}
              className="bg-[#00CCFF] text-black hover:bg-[#00E5FF] font-mono uppercase tracking-wider px-4 py-2 rounded-md"
            >
              Join Free
            </Button>
          </div>
        </div>
      </header>

      {/* ============= ON-AIR NOW TICKER (anonymous → listener funnel) ============= */}
      <OnAirNowTicker fixed topOffset="72px" />

      {/* ============= HERO SECTION ============= */}
      <section
        className="dark-surface relative min-h-screen flex items-center pt-16"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />
          {seasonal.isHoliday && (
            <div className={`absolute inset-0 bg-gradient-to-br ${seasonal.heroGradient} pointer-events-none`} />
          )}
        </div>

        {/* Content */}
        <div className="relative z-10 w-full px-4 md:px-8 py-12">
          <div className="max-w-xl mx-auto md:mx-0 md:ml-8 lg:ml-16">
            
            {/* Seasonal holiday banner */}
            {seasonal.isHoliday && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono tracking-widest mb-4"
                style={{
                  borderColor: `${seasonal.accent}40`,
                  color: seasonal.accent,
                  background: `${seasonal.accent}15`,
                  border: `1px solid ${seasonal.accent}40`
                }}
                data-testid="seasonal-banner-landing"
              >
                {seasonal.label}
              </div>
            )}

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-400/30 to-gray-300/30 rounded-full mb-6">
              <span className="w-2 h-2 bg-[#00ff88] rounded-full" />
              <span className="text-white/90 font-mono text-xs tracking-wider uppercase">
                Independent Music Distribution
              </span>
            </div>

            {/* Main Headline */}
            <h1
              className="glow-h1 text-4xl md:text-5xl lg:text-6xl font-['Chakra_Petch'] font-bold uppercase leading-tight mb-6"
              style={{ '--glow-h1-color': '#00ff88' }}
              data-testid="landing-hero-title"
            >
              <span className="text-white">Your Music.</span>
              <br />
              <span className="bg-gradient-to-r from-[#00ff88] to-[#FFDA33] bg-clip-text text-transparent">
                Your Revenue.
              </span>
            </h1>

            {/* Sub-headline — iter_493 slogan */}
            <p className="text-white/90 text-base md:text-lg font-sans mb-3 leading-relaxed font-['Chakra_Petch'] uppercase tracking-wider" data-testid="landing-slogan">
              <span className="text-[#00CCFF]">One voice.</span>{' '}
              <span className="text-[#FFDA33]">Your choice.</span>{' '}
              <span className="text-[#00ff88]">Press play.</span>{' '}
              <span className="text-[#A855F7]">Get paid.</span>
            </p>
            <p className="text-white/80 text-sm md:text-base font-sans mb-8 leading-relaxed">
              Keep ~90% of every sale. Distribute globally. Own your masters. No middlemen.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                data-testid="start-free-btn"
                onClick={() => navigate('/register')}
                className="bg-[#00CCFF] text-black hover:bg-[#00E5FF] font-['Chakra_Petch'] uppercase tracking-wider px-8 py-6 text-lg rounded-md flex items-center justify-center gap-2"
              >
                Start Free
                <ArrowRight size={20} />
              </Button>
              <Button
                data-testid="view-pricing-btn"
                variant="outline"
                onClick={scrollToPricing}
                className="border-2 border-[#FFDA33] text-[#FFDA33] hover:bg-[#FFDA33]/10 font-['Chakra_Petch'] uppercase tracking-wider px-8 py-6 text-lg rounded-md"
              >
                View Pricing
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Artist Circles */}
              <div className="flex -space-x-2">
                {artistCircles.map((letter, index) => (
                  <div
                    key={letter}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00CCFF] to-[#9b59b6] flex items-center justify-center text-white font-bold text-sm border-2 border-black"
                    style={{ zIndex: artistCircles.length - index }}
                  >
                    {letter}
                  </div>
                ))}
              </div>
              
              {/* Join Count */}
              <p className="text-white font-['Chakra_Petch'] font-bold text-base">
                Join 10,000+ Artists
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============= ALL-GENRE LIVE RADIO HERO (cinematic) ============= */}
      <AllGenreRadioHero />

      {/* ============= PLATFORM FEATURES (Expandable) ============= */}
      <section className="px-4 md:px-8 py-10 md:py-20 border-t border-[#1a1a1a] bg-[#050505]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-[#FFDA33] font-mono text-sm tracking-widest uppercase mb-2 block">
              Everything You Need
            </span>
            <h2 className="text-4xl md:text-5xl font-['Chakra_Petch'] text-white uppercase">
              Platform Features
            </h2>
          </div>
          
          {/* Expandable Feature Cards */}
          <div className="space-y-4">
            {platformFeatures.map((feature) => {
              const isExpanded = expandedFeature === feature.id;
              const Icon = feature.icon;
              
              return (
                <div
                  key={feature.id}
                  className={`border transition-all duration-300 overflow-hidden ${
                    isExpanded 
                      ? 'border-opacity-100' 
                      : 'border-[#1a1a1a] hover:border-opacity-50'
                  }`}
                  style={{ borderColor: isExpanded ? feature.color : undefined }}
                  data-testid={`feature-card-${feature.id}`}
                >
                  {/* Header - Always Visible */}
                  <button
                    onClick={() => toggleFeature(feature.id)}
                    className="w-full p-6 flex items-center gap-6 text-left hover:bg-white/5 transition-colors"
                    data-testid={`feature-toggle-${feature.id}`}
                  >
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <Icon size={28} style={{ color: feature.color }} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-['Chakra_Petch'] text-white uppercase mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-[#888] font-mono text-sm">
                        {feature.shortDesc}
                      </p>
                    </div>
                    
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-transform"
                      style={{ 
                        backgroundColor: isExpanded ? feature.color : '#1a1a1a',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    >
                      <ChevronDown size={20} className={isExpanded ? 'text-black' : 'text-white'} />
                    </div>
                  </button>
                  
                  {/* Expanded Content */}
                  <div 
                    className={`transition-all duration-300 ${
                      isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 pt-2">
                      <div className="border-t border-[#1a1a1a] pt-6">
                        <p className="text-white/80 font-sans mb-6 leading-relaxed">
                          {feature.fullDesc}
                        </p>
                        
                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-4">
                          {feature.stats.map((stat) => (
                            <div 
                              key={stat.label}
                              className="text-center p-4 bg-black/50 border border-[#1a1a1a]"
                            >
                              <p 
                                className="text-2xl font-['Chakra_Petch'] font-bold mb-1"
                                style={{ color: feature.color }}
                              >
                                {stat.value}
                              </p>
                              <p className="text-[#666] font-mono text-xs uppercase">
                                {stat.label}
                              </p>
                            </div>
                          ))}
                        </div>
                        
                        {/* CTA */}
                        <Button
                          onClick={() => navigate('/register')}
                          className="mt-6 w-full font-['Chakra_Petch'] uppercase"
                          style={{ backgroundColor: feature.color, color: '#000' }}
                        >
                          Try {feature.title} Free
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= ARTIST SPOTLIGHT ============= */}
      <section className="px-4 md:px-8 py-10 md:py-20 border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-[#FFDA33] font-mono text-sm tracking-widest uppercase mb-2 block">
              Featured Artists
            </span>
            <h2 className="text-4xl md:text-5xl font-['Chakra_Petch'] text-white uppercase flex items-center justify-center gap-3">
              <Star className="text-[#FFDA33]" size={32} />
              Artist Spotlight
            </h2>
          </div>
          <CreatorSpotlight />
        </div>
      </section>

      {/* ============= FEATURED PROMOTIONS ============= */}
      <FeaturedPromotions />

      {/* ============= ONEVOICE RADAR — algorithmic fresh-tracks feed ============= */}
      <section className="px-4 md:px-8 py-10 bg-black border-t border-[#1a1a1a]">
        <OneVoiceRadarStrip />
      </section>

      {/* ============= UPCOMING SESSIONS — next curators going live ============= */}
      <section className="px-4 md:px-8 py-10 bg-black border-t border-[#1a1a1a]">
        <UpcomingSessionsStrip />
      </section>

      {/* ============= SONG WARS CHAMPIONS ============= */}
      <section className="bg-black border-t border-[#1a1a1a]">
        <SongWarsChampionsSlideshow />
      </section>

      {/* ============= PRICING ============= */}
      <section id="pricing-section" className="px-4 md:px-8 py-10 md:py-20 border-t border-[#1a1a1a] bg-[#050505] relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-1/2 left-0 w-1/2 h-96 bg-gradient-to-r from-[#00CCFF]/5 to-transparent blur-[100px]" />
        <div className="absolute top-1/2 right-0 w-1/2 h-96 bg-gradient-to-l from-[#FFDA33]/5 to-transparent blur-[100px]" />
        
        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-[#00ff88] font-mono text-sm tracking-widest uppercase mb-2 block">
              Transparent & Fair
            </span>
            <h2 className="text-4xl md:text-5xl font-['Chakra_Petch'] text-white uppercase mb-4">
              Simple Pricing
            </h2>
            <p className="text-[#888] font-mono text-lg">
              No hidden fees. <span className="text-[#00ff88]">Keep ~90%</span> of every sale.
            </p>
          </div>
          
          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Tracks */}
            <div className="group relative border border-[#1a1a1a] bg-black/50 p-8 hover:border-[#00CCFF]/50 transition-all duration-300 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-b from-[#00CCFF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-16 h-16 bg-[#00CCFF]/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <Music className="text-[#00CCFF]" size={32} />
                </div>
                <h3 className="text-2xl font-['Chakra_Petch'] text-white text-center mb-2">Tracks</h3>
                <div className="text-center mb-6">
                  <span className="text-5xl font-['Chakra_Petch'] font-bold text-[#FFDA33]">$1.99</span>
                  <span className="text-[#666] font-mono text-sm block mt-1">per track</span>
                </div>
                <ul className="space-y-3 text-[#888] font-mono text-sm mb-8">
                  <li className="flex items-center gap-2">
                    <Play size={14} className="text-[#00CCFF]" /> Unlimited streaming
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart size={14} className="text-[#00CCFF]" /> Fan purchases to own
                  </li>
                  <li className="flex items-center gap-2">
                    <DollarSign size={14} className="text-[#00CCFF]" /> ~90% revenue share
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/register')}
                  variant="outline"
                  className="w-full border-[#00CCFF] text-[#00CCFF] hover:bg-[#00CCFF] hover:text-black font-['Chakra_Petch'] uppercase"
                >
                  Start Selling
                </Button>
              </div>
            </div>
            
            {/* Videos - Featured */}
            <div className="group relative border-2 border-[#00CCFF] bg-black/50 p-8 md:-mt-4 md:mb-4 transition-all duration-300 hover:-translate-y-2">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#00CCFF] text-black px-4 py-1 font-mono text-xs uppercase tracking-wider">
                Most Popular
              </div>
              <div className="absolute inset-0 bg-gradient-to-b from-[#00CCFF]/10 to-transparent" />
              <div className="relative">
                <div className="w-16 h-16 bg-[#00CCFF]/20 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <Video className="text-[#00CCFF]" size={32} />
                </div>
                <h3 className="text-2xl font-['Chakra_Petch'] text-white text-center mb-2">Videos</h3>
                <div className="text-center mb-6">
                  <span className="text-5xl font-['Chakra_Petch'] font-bold text-[#FFDA33]">$3.59</span>
                  <span className="text-[#666] font-mono text-sm block mt-1">per video</span>
                </div>
                <ul className="space-y-3 text-[#888] font-mono text-sm mb-8">
                  <li className="flex items-center gap-2">
                    <Play size={14} className="text-[#00CCFF]" /> HD streaming
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart size={14} className="text-[#00CCFF]" /> Download to own
                  </li>
                  <li className="flex items-center gap-2">
                    <DollarSign size={14} className="text-[#00CCFF]" /> ~90% revenue share
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/register')}
                  className="w-full bg-[#00CCFF] text-black hover:bg-[#00E5FF] font-['Chakra_Petch'] uppercase"
                >
                  Start Selling
                </Button>
              </div>
            </div>
            
            {/* Beats */}
            <div className="group relative border border-[#1a1a1a] bg-black/50 p-8 hover:border-[#FF7F50]/50 transition-all duration-300 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-b from-[#FF7F50]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-16 h-16 bg-[#FF7F50]/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <Disc className="text-[#FF7F50]" size={32} />
                </div>
                <h3 className="text-2xl font-['Chakra_Petch'] text-white text-center mb-2">Beats</h3>
                <div className="text-center mb-6">
                  <span className="text-5xl font-['Chakra_Petch'] font-bold text-[#FFDA33]">$54.99</span>
                  <span className="text-[#666] font-mono text-sm block mt-1">starting price</span>
                </div>
                <ul className="space-y-3 text-[#888] font-mono text-sm mb-8">
                  <li className="flex items-center gap-2">
                    <Users size={14} className="text-[#FF7F50]" /> License tiers
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield size={14} className="text-[#FF7F50]" /> Exclusive options
                  </li>
                  <li className="flex items-center gap-2">
                    <DollarSign size={14} className="text-[#FF7F50]" /> ~90% revenue share
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/register')}
                  variant="outline"
                  className="w-full border-[#FF7F50] text-[#FF7F50] hover:bg-[#FF7F50] hover:text-black font-['Chakra_Petch'] uppercase"
                >
                  Start Selling
                </Button>
              </div>
            </div>
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <p className="text-[#666] font-mono text-sm mb-6">
              Free to join. No monthly fees. Only pay when you sell.
            </p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#00CCFF] text-black hover:bg-[#00E5FF] font-['Chakra_Petch'] uppercase tracking-wider px-12 py-6 text-lg rounded-md"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </section>

      {/* ============= NEWSLETTER ============= */}
      <section className="px-4 md:px-8 py-12 border-t border-[#1a1a1a]">
        <div className="max-w-2xl mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      {/* ============= FOOTER ============= */}
      <footer className="px-4 md:px-8 py-8 border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#888888] text-sm font-mono text-center md:text-left" data-testid="landing-copyright">
            &copy; {new Date().getFullYear()} ONE VOICE DISTRIBUTION™ // ALL RIGHTS RESERVED
            <br className="md:hidden" />
            <span className="text-[#666] text-[10px] block mt-1">
              OneVoice™, OneVoice Radio™, OneVoice Studio™, AI Studio™, Song Wars™ &amp; Music Review™ are trademarks of One Voice Distribution.
            </span>
          </p>
          <div className="flex items-center gap-2 text-[#666] text-xs">
            <img 
              src="https://www.emergentagent.com/favicon.ico" 
              alt="Emergent"
              className="w-4 h-4 opacity-50"
            />
            <span className="font-mono">Made with Emergent</span>
          </div>
        </div>
      </footer>
      
    </div>
  );
}
